// ============================================
// Creator Editor - Lorebook
// 로어북 관리 모듈
// ============================================

import { showToast, escapeHtml } from '../../utils.js';
import { showConfirm } from '../../shared/confirm-modal.js';
import {
  getLorebookEntries,
  getEditingLorebookIndex,
  setLorebookEntries,
  addLorebookEntry,
  updateLorebookEntry,
  removeLorebookEntry,
  setEditingLorebookIndex,
  MAX_LOREBOOK_ENTRIES
} from '../state/index.js';
import { updatePublishButtonState } from './events.js';

// 현재 편집 중인 키워드 배열
let currentKeywords = [];

// ============================================
// 아코디언 토글
// ============================================

export function toggleLorebookSection() {
  const section = document.getElementById('lorebookSection');
  const content = document.getElementById('lorebookSectionContent');
  if (!section || !content) return;

  section.classList.toggle('expanded');
  content.classList.toggle('expanded');
  content.classList.toggle('collapsed');
}

// ============================================
// 리스트 렌더링
// ============================================

export function renderLorebookList() {
  const listEl = document.getElementById('lorebookList');
  const countEl = document.getElementById('lorebookCount');
  if (!listEl) return;

  const entries = getLorebookEntries();

  if (countEl) {
    countEl.textContent = entries.length;
  }

  if (entries.length === 0) {
    listEl.innerHTML = '';
    return;
  }

  listEl.innerHTML = entries.map((entry, index) => {
    const keywordsText = (entry.keywords || []).slice(0, 3).join(', ');
    const disabledClass = entry.enabled === false ? ' disabled' : '';

    return `
      <div class="advanced-list-item${disabledClass}" data-index="${index}" draggable="true">
        <div class="advanced-list-item-drag">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <circle cx="9" cy="5" r="1.5"/><circle cx="15" cy="5" r="1.5"/>
            <circle cx="9" cy="12" r="1.5"/><circle cx="15" cy="12" r="1.5"/>
            <circle cx="9" cy="19" r="1.5"/><circle cx="15" cy="19" r="1.5"/>
          </svg>
        </div>
        <span class="advanced-list-item-number">${index + 1}</span>
        <div class="advanced-list-item-content" data-action="editLorebook" data-index="${index}">
          <div class="advanced-list-item-title">${escapeHtml(entry.name)}</div>
          <div class="advanced-list-item-desc">${escapeHtml(keywordsText)}</div>
        </div>
        <div class="advanced-list-item-actions">
          <button class="advanced-delete-btn" data-action="deleteLorebook" data-index="${index}">삭제</button>
        </div>
      </div>
    `;
  }).join('');

  initLorebookDragAndDrop(listEl);
}

// 드래그 앤 드롭 초기화
function initLorebookDragAndDrop(listEl) {
  let draggedItem = null;
  let draggedIndex = null;

  listEl.querySelectorAll('.advanced-list-item').forEach(item => {
    item.addEventListener('dragstart', (e) => {
      draggedItem = item;
      draggedIndex = parseInt(item.dataset.index);
      item.classList.add('dragging');
      e.dataTransfer.effectAllowed = 'move';
    });

    item.addEventListener('dragend', () => {
      item.classList.remove('dragging');

      // 새 순서 저장
      const items = [...listEl.querySelectorAll('.advanced-list-item')];
      const newOrder = items.map(el => parseInt(el.dataset.index));

      // 순서가 변경되었는지 확인
      const isChanged = newOrder.some((idx, i) => idx !== i);
      if (isChanged) {
        reorderLorebookEntries(newOrder);
        renderLorebookList();
      }

      draggedItem = null;
      draggedIndex = null;
    });

    item.addEventListener('dragover', (e) => {
      e.preventDefault();
      if (!draggedItem || draggedItem === item) return;

      const rect = item.getBoundingClientRect();
      const midY = rect.top + rect.height / 2;

      if (e.clientY < midY) {
        item.parentNode.insertBefore(draggedItem, item);
      } else {
        item.parentNode.insertBefore(draggedItem, item.nextSibling);
      }
    });
  });
}

// 로어북 순서 변경
function reorderLorebookEntries(newOrder) {
  const entries = getLorebookEntries();
  const reordered = newOrder.map(idx => entries[idx]);
  setLorebookEntries(reordered);
}

// ============================================
// 모달 관리
// ============================================

export function openLorebookModal(editIndex = null) {
  const entries = getLorebookEntries();

  // 새로 추가할 때 최대 개수 체크
  if (editIndex === null && entries.length >= MAX_LOREBOOK_ENTRIES) {
    showToast(`로어북은 최대 ${MAX_LOREBOOK_ENTRIES}개까지 추가할 수 있어요`);
    return;
  }

  setEditingLorebookIndex(editIndex);

  const modal = document.getElementById('lorebookEditModal');
  const nameInput = document.getElementById('lorebookName');
  const contentInput = document.getElementById('lorebookContent');
  const enabledToggle = document.getElementById('lorebookEnabled');
  const titleEl = document.getElementById('lorebookModalTitle');
  const deleteSection = document.getElementById('lorebookDeleteSection');
  const nameCountEl = document.getElementById('lorebookNameCount');
  const contentCountEl = document.getElementById('lorebookContentCount');

  if (editIndex !== null && entries[editIndex]) {
    // 편집 모드
    const entry = entries[editIndex];
    nameInput.value = entry.name || '';
    contentInput.value = entry.content || '';
    enabledToggle.checked = entry.enabled !== false;
    currentKeywords = [...(entry.keywords || [])];
    titleEl.textContent = '로어북 편집';
    deleteSection.style.display = 'block';
  } else {
    // 새로 추가 모드
    nameInput.value = '';
    contentInput.value = '';
    enabledToggle.checked = true;
    currentKeywords = [];
    titleEl.textContent = '로어북 추가';
    deleteSection.style.display = 'none';
  }

  // 키워드 태그 렌더링
  renderKeywordTags();

  // 글자 수 카운터 업데이트
  if (nameCountEl) nameCountEl.textContent = nameInput.value.length;
  if (contentCountEl) contentCountEl.textContent = contentInput.value.length;

  // 모달 표시
  modal?.offsetHeight; // reflow
  modal?.classList.add('active');

  if (window.ModalManager) {
    window.ModalManager.push('lorebookEditModal');
  }
}

export function closeLorebookModal() {
  const modal = document.getElementById('lorebookEditModal');
  modal?.classList.remove('active');

  if (window.ModalManager) {
    window.ModalManager.remove('lorebookEditModal', true);
  }

  setEditingLorebookIndex(null);
  currentKeywords = [];
}

export function saveLorebookEntryData() {
  const nameInput = document.getElementById('lorebookName');
  const contentInput = document.getElementById('lorebookContent');
  const enabledToggle = document.getElementById('lorebookEnabled');

  const name = nameInput?.value?.trim();
  const content = contentInput?.value?.trim();
  const enabled = enabledToggle?.checked ?? true;

  if (!name) {
    showToast('로어 이름을 입력해 주세요');
    nameInput?.focus();
    return;
  }

  if (currentKeywords.length === 0) {
    showToast('트리거 키워드를 하나 이상 입력해 주세요');
    document.getElementById('lorebookKeywordInput')?.focus();
    return;
  }

  if (!content) {
    showToast('로어 내용을 입력해 주세요');
    contentInput?.focus();
    return;
  }

  const entryData = {
    name,
    keywords: currentKeywords,
    content,
    enabled
  };

  const editIndex = getEditingLorebookIndex();
  if (editIndex !== null) {
    updateLorebookEntry(editIndex, entryData);
    showToast('로어북이 수정됐어요');
  } else {
    addLorebookEntry(entryData);
    showToast('로어북이 추가됐어요');
  }

  renderLorebookList();
  closeLorebookModal();
  updatePublishButtonState();
}

export async function deleteLorebookEntryData() {
  const confirmed = await showConfirm('이 로어북 항목을 삭제할까요?');
  if (!confirmed) return;

  const editIndex = getEditingLorebookIndex();
  if (editIndex !== null) {
    removeLorebookEntry(editIndex);
    renderLorebookList();
    closeLorebookModal();
    showToast('로어북이 삭제됐어요');
    updatePublishButtonState();
  }
}

// 리스트에서 직접 삭제
export async function deleteLorebookEntry(index) {
  const confirmed = await showConfirm('이 로어북 항목을 삭제할까요?');
  if (!confirmed) return;

  removeLorebookEntry(index);
  renderLorebookList();
  showToast('로어북이 삭제됐어요');
  updatePublishButtonState();
}

// ============================================
// 키워드 태그 관리
// ============================================

function renderKeywordTags() {
  const tagsContainer = document.getElementById('lorebookKeywordTags');
  if (!tagsContainer) return;

  tagsContainer.innerHTML = currentKeywords.map((keyword, index) => `
    <span class="keyword-tag">
      ${escapeHtml(keyword)}
      <span class="keyword-tag-remove" data-keyword-index="${index}">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
          <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </span>
    </span>
  `).join('');
}

export function addKeyword(keyword) {
  const trimmed = keyword.trim();
  if (!trimmed) return false;

  if (trimmed.length > 50) {
    showToast('키워드는 50자 이하로 입력해 주세요');
    return false;
  }

  if (currentKeywords.includes(trimmed)) {
    showToast('이미 추가된 키워드예요');
    return false;
  }

  currentKeywords.push(trimmed);
  renderKeywordTags();
  return true;
}

export function removeKeyword(index) {
  currentKeywords.splice(index, 1);
  renderKeywordTags();
}

// ============================================
// 초기화 (글자 수 카운터 등)
// ============================================

export function initLorebookCounters() {
  const nameInput = document.getElementById('lorebookName');
  const nameCountEl = document.getElementById('lorebookNameCount');
  const contentInput = document.getElementById('lorebookContent');
  const contentCountEl = document.getElementById('lorebookContentCount');

  if (nameInput && nameCountEl) {
    nameInput.addEventListener('input', () => {
      nameCountEl.textContent = nameInput.value.length;
    });
  }

  if (contentInput && contentCountEl) {
    contentInput.addEventListener('input', () => {
      contentCountEl.textContent = contentInput.value.length;
    });
  }
}
